import mapgl from 'maplibre-gl';
import { useEffect, useCallback, useState } from "react";
import { toMercator } from '@turf/projection';
import { point } from '@turf/helpers';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import xmark from './xmark-solid.svg';

// The callback function has to be wrapped with useCallback hook to keep it not being changed
// before setting it off. The function has to be exactly the same to make the functionality works properly.

// These variables cannot be in the scope of function
// because they will be reset every time the component re-rendering, 
// and the callback of the map behavior map.on(action, callback) will be re-instantiated,
// which make it not able to be turned off.

let marker = null;
let assetUrl = "";
let crossSymbol = {};

export function Inspector({ map, isOpen, layer }) {

  const [layerId, setLayerId] = useState(null);
  const [subLayerIds, setsubLayerIds] = useState([]);
  const [featureProperties, setFeatureProperties] = useState({});
  const [pixelProperties, setPixelProperties] = useState({});

  // construct endpoint for fetch request
  const constructRequest = (point, url, expression) => {
    const endpointUrl = 'https://ou60yuvp0i.execute-api.us-east-1.amazonaws.com/cog/point';
    const params = {
      'url': encodeURIComponent(url),
      'coord-crs': encodeURIComponent("epsg:3857"),
      'expression': encodeURIComponent(expression)
    }
    const url_params = Object.keys(params).map(i => `${i}=${params[i]}`).join('&');
    const endpoint = `${endpointUrl}/${point[0]},${point[1]}?${url_params}`;
    return endpoint
  }

  // Converts a WGS84 GeoJSON object into Mercator (EPSG:900913) projection
  const lngLat2mercator = (lngLat) => {
    const ptWgs84 = point([lngLat['lng'], lngLat['lat']]);
    const ptMerc = toMercator(ptWgs84);
    // [0] and [1]
    return ptMerc['geometry']['coordinates'];
  }

  const addCustomMarker = (coordinates) => {
    // remove if exists
    marker && marker?.remove();
    // add to map
    marker = new mapgl.Marker(crossSymbol)
      .setLngLat(coordinates)
      .addTo(map?.current);
  }

  const removeCustomMarker = () => {
    if (marker) {
      marker?.remove();
      marker = null;
    }
  }

  // fetch pixel info from endpoint
  const fetchPixelInfo = async (point, url, coordinates) => {
    const expression = "b1";
    const endpoint = constructRequest(point, url, expression);
    const res = await fetch(endpoint);
    if (res.ok) {
      const data = await res.json();
      // no solution to differentiate pixels and background from raster layer for now
      // return nothing if value equals 99
      if (data?.values[0] < 99) {
        setPixelProperties(data);
        addCustomMarker(coordinates);
      } else {
        setPixelProperties({});
        removeCustomMarker();
      }
    }
  }

  // click event works on raster layer
  // https://docs.mapbox.com/mapbox-gl-js/example/mouse-position/
  const rasterClickEventListener = useCallback((e) => {
    const [x, y] = lngLat2mercator(e.lngLat);
    fetchPixelInfo([x, y], assetUrl, e.lngLat);
  }, [map]);

  // the function should be exactly the same, otherwise it won't be disabled
  const vectorClickEventListener = useCallback((e) => {
    if (e?.features?.length > 0) {
      // Copy coordinates array.
      const coordinates = e.features[0].geometry.coordinates.slice();
      setFeatureProperties(e.features[0].properties);
    }
  }, [map]);

  // change cursor to pinter when inspector enabled with map.off([event], [layer], [listener])
  const mouseEnterEventListener = useCallback(() => {
    map.current.getCanvas().style.cursor = 'pointer';
  }, [map]);

  // change cursor back when inspector disabled
  const mouseLeaveEventListener = useCallback(() => {
    map.current.getCanvas().style.cursor = '';
  }, [map]);

  // enable inspector functionalities
  const enableInspector = (layer) => {
    if (layer?.assetType === "fic") {
      // add click event
      map.current.on('click', rasterClickEventListener);
    } else if (layer?.assetType === "gene_group") {
      layer?.subLayerIds?.map(layerId => {
        // add click event
        map.current.on('click', layerId, vectorClickEventListener);
        // Change the cursor to a pointer when the mouse is over the places layer.
        map.current.on('mouseenter', layerId, mouseEnterEventListener);
        // Change it back to a pointer when it leaves.
        map.current.on('mouseleave', layerId, mouseLeaveEventListener);
      });
    } else {
      // add click event
      map.current.on('click', layer.id, vectorClickEventListener);
      // Change the cursor to a pointer when the mouse is over the places layer.
      map.current.on('mouseenter', layer.id, mouseEnterEventListener);
      // Change it back to a pointer when it leaves.
      map.current.on('mouseleave', layer.id, mouseLeaveEventListener);
    }
  }

  // disable inspector functionalities
  const disableInspector = (layerId) => {
    if (layerId.includes("fic")) {
      // add click event
      map?.current.off('click', rasterClickEventListener);
    } else {
      // add click event
      map?.current.off('click', layerId, vectorClickEventListener);
      // Change the cursor to a pointer when the mouse is over the places layer.
      map?.current.off('mouseenter', layerId, mouseEnterEventListener);
      // Change it back to a pointer when it leaves.
      map?.current.off('mouseleave', layerId, mouseLeaveEventListener);
    }
  }

  // enable inspection functions only when tab is open and a layer is chosen
  useEffect(() => {
    // update assetUrl
    assetUrl = layer?.assetUrl;
    // disable inspector for preivous layer
    subLayerIds.length > 1 && subLayerIds.map(layerId => disableInspector(layerId));
    setsubLayerIds([]);
    layerId && disableInspector(layerId);
    setLayerId(null);
    // enable inpector for the chosen layer
    if (isOpen && layer && map?.current) {
      if (layer.assetType === "gene_group") {
        setLayerId(null);
        setsubLayerIds(layer.subLayerIds);
      } else {
        setLayerId(layer.id);
        setsubLayerIds([]);
      }
      enableInspector(layer);
    }
  }, [isOpen, layer])

  // reset properties when changing active layer or re-opening inspect tab
  useEffect(() => {
    removeCustomMarker();
    setFeatureProperties({});
    setPixelProperties({});
  }, [layerId, isOpen])

  // create a custom marker with svg file
  useEffect(() => {
    fetch(xmark)
      .then(response => response.text())
      .then(data => {
        // create a DOM element for the custom marker
        const el = document.createElement('div');
        el.className = 'CustomMarker';
        el.innerHTML = data;
        el.style.fill = "white";
        el.style.width = `30px`;
        el.style.height = `30px`;
        crossSymbol = { element: el };
      }).catch((error) => {
        console.log("Error creating custom Cross Symbol DOM, a default marker will be used.");
      });
  }, [])

  return (
    <Box sx={{ width: '100%', mx: '1rem' }}>
      {layer && layer?.assetType === "fic" ? <PixelPropertyComponent pixelProperties={pixelProperties} /> : <VectorPropertyComponent featureProperties={featureProperties} />}
      <MouseHover map={map} isOpen={isOpen} layer={layer} />
    </Box>
  )
}

function PixelPropertyComponent({ pixelProperties }) {
  return (
    <div>
      <Typography variant="body1" align="left" gutterBottom>Coordinates:</Typography>
      <Typography variant="body2" align="left" gutterBottom>X: {pixelProperties?.coordinates && pixelProperties?.coordinates[0]}</Typography>
      <Typography variant="body2" align="left" gutterBottom>Y: {pixelProperties?.coordinates && pixelProperties?.coordinates[1]}</Typography>
      <Typography variant="body1" align="left" gutterBottom>Value: {pixelProperties?.values && pixelProperties?.values[0]}</Typography>
      <Typography variant="body1" align="left" gutterBottom>Band: {pixelProperties?.band_names && pixelProperties?.band_names[0]}</Typography>
    </div>
  )
}

function VectorPropertyComponent({ featureProperties }) {
  return (
    <div>
      {Object.keys(featureProperties).map((key) => (
        <Typography key={"key_".concat(key)} variant="body1" align="left" gutterBottom>
          <strong>{key}:</strong> {featureProperties[key]}
        </Typography>
      ))}
    </div>
  )
}

// To make hover effect working, the fill-opacity property has to be set appropriately
// in addPMTilelayers.js
// The feature-state dependent fill-opacity expression will render the hover effect
// when a feature's hover state is set to true.
// hover effect only works on vector layers rather than raster layers
export function MouseHover({ map, isOpen, layer }) {

  const [layerId, setLayerId] = useState(null);
  const [subLayerIds, setsubLayerIds] = useState([]);
  let hoveredStateId = null;
  let sourceId = null;
  let sourceLayerId = null;

  const setHoverStateOn = (source, sourceLayer, featureId) => {
    map?.current?.setFeatureState(
      {
        source: source,
        sourceLayer: sourceLayer,
        id: featureId
      },
      { hover: true }
    );
  }
  const setHoverStateOff = (source, sourceLayer, featureId) => {
    map?.current?.setFeatureState(
      {
        source: source,
        sourceLayer: sourceLayer,
        id: featureId
      },
      { hover: false }
    );
  }

  // event listener has to be exactly the same
  // cache function to avoid failure deregistering listener
  const mouseMoveListener = useCallback((e) => {
    if (e.features.length > 0) {
      const featureId = e.features[0].id;
      const source = e.features[0].source;
      const sourceLayer = e.features[0].sourceLayer;

      if (hoveredStateId) {
        setHoverStateOff(sourceId, sourceLayerId, hoveredStateId);
      }
      setHoverStateOn(source, sourceLayer, featureId);
      hoveredStateId = featureId;
      sourceId = source;
      sourceLayerId = sourceLayer;
    }
  }, [map]);

  const mouseLeaveListener = useCallback(() => {
    if (hoveredStateId && sourceId && sourceLayerId) {
      // setHoverStateOff(layerId, hoveredStateId);
      map?.current?.setFeatureState(
        { source: sourceId, sourceLayer: sourceLayerId, id: hoveredStateId },
        { hover: false }
      );
    }
  }, [map]);

  useEffect(() => {
    if (layerId) {
      map?.current?.off("mousemove", layerId, mouseMoveListener);
      map?.current?.off("mouseleave", layerId, mouseLeaveListener);
    }
    if (subLayerIds.length > 1) {
      subLayerIds.map(layerId => {
        map?.current?.off("mousemove", layerId, mouseMoveListener);
        map?.current?.off("mouseleave", layerId, mouseLeaveListener);
      })
    }
    if (isOpen && layer && map?.current && layer?.assetType !== "fic") {
      if (layer.assetType === "gene_group") {
        layer.subLayerIds.map(layerId => {
          map?.current?.on("mousemove", layerId, mouseMoveListener);
          map?.current?.on("mouseleave", layerId, mouseLeaveListener);
        })
        setLayerId(null);
        setsubLayerIds(layer.subLayerIds);
      } else {
        map?.current?.on("mousemove", layer.id, mouseMoveListener);
        map?.current?.on("mouseleave", layer.id, mouseLeaveListener);
        setLayerId(layer.id);
        setsubLayerIds([]);
      }

    }
  }, [isOpen, layer])
}